// A new brand's setup as one focused flow (Brúnó, 2026-10-11): company
// details, the product creators will film (2026-10-15), the agreement, the
// plan, then the portal. Until then there is no
// menu to wander into: every page that needs the setup would only send the
// brand back to it (Billing opened Company). The steps show above the page as
// circles on one track, the finished part green; a finished step can be
// opened again, a later one cannot yet. Since the same day the steps sit in
// the dark top bar, which stays on screen while the page scrolls, and every
// step's Back and Continue sit in one floating island at the foot of the
// window, in the same place on all three (Brúnó: "egyforma helyen legyenek a
// tovább gombok", then "a floating sticky island a little narrower").

import {createContext, useContext, useState} from 'react';
import type {ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {Link, NavLink, useLocation} from 'react-router-dom';
import type {BrandAppData} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';

export type SetupStep = 'company' | 'product' | 'agreement' | 'plan';

export const SETUP_STEPS: readonly {key: SetupStep; label: string; hint: string; path: string}[] = [
  {key: 'company', label: 'Company details', hint: 'About 2 minutes', path: '/company'},
  {key: 'product', label: 'Product', hint: 'What creators film', path: '/products'},
  {key: 'agreement', label: 'Agreement', hint: 'Read and accept', path: '/agreement'},
  {key: 'plan', label: 'Plan', hint: 'Basic or Enterprise', path: '/plan'},
];

/**
 * The step a brand is on, or null once it can use the portal. A brand from
 * before plans existed, with campaigns already, is not sent back into setup
 * for the plan: it keeps its work and is asked for a plan in place.
 */
export function setupStep(data: BrandAppData): SetupStep | null {
  if (!data.profile.complete) return 'company';
  if (!data.products.length) return 'product';
  if (data.agreement.status !== 'signed') return 'agreement';
  if (!data.plan.key && data.campaigns.length === 0) return 'plan';
  return null;
}

/** The setup pages the brand may open on `step`: the ones done, and this one. */
export function setupPaths(step: SetupStep): string[] {
  const at = SETUP_STEPS.findIndex((item) => item.key === step);
  return SETUP_STEPS.slice(0, at + 1).map((item) => item.path);
}

export function SetupShell({data, step, onSignOut, children}: {
  data: BrandAppData; step: SetupStep; onSignOut(): void; children: ReactNode;
}) {
  const at = SETUP_STEPS.findIndex((item) => item.key === step);
  const [footer, setFooter] = useState<HTMLElement | null>(null);
  return <FooterSlot.Provider value={footer}><div className="setup-app">
    {/* One bar for the brand, the steps and the account, sticky, so the steps
        stay in sight down a long form instead of taking a band of their own. */}
    <header className="setup-top">
      <Link className="brand" to="/"><img src="/img/logo-mark.webp?v=4" alt="" />earnby<em>content</em></Link>
      <nav className="setup-steps" aria-label="Setup"><ol>{SETUP_STEPS.map((item, index) => {
        const state = index < at ? 'done' : index === at ? 'current' : 'next';
        const inner = <>
          <i className="setup-dot" aria-hidden="true">{state === 'done' ? <Icon name="check" size={14} /> : index + 1}</i>
          <span className="setup-step-text"><b>{item.label}</b><small>{state === 'done' ? 'Done' : item.hint}</small></span>
        </>;
        const name = `${item.label}, step ${index + 1} of ${SETUP_STEPS.length}${state === 'done' ? ', done' : ''}`;
        return <li className={`setup-step ${state}`} key={item.key}>{state === 'next'
          ? <span className="setup-step-link" aria-disabled="true" aria-label={name}>{inner}</span>
          : <NavLink className={({isActive}) => `setup-step-link${isActive ? ' open' : ''}`} to={item.path} aria-label={name} aria-current={state === 'current' ? 'step' : undefined}>{inner}</NavLink>}</li>;
      })}</ol></nav>
      <div className="setup-account">
        <span className="setup-who">{data.me.email}</span>
        <button className="text-button" type="button" onClick={onSignOut}>Sign out</button>
      </div>
    </header>
    <main className="setup-main">{data.sandbox ? <div className="sandbox-strip" role="status"><b>Sandbox.</b> Test data: nothing here reaches real creators.</div> : null}{children}</main>
    <footer className="setup-footer" ref={setFooter} />
  </div></FooterSlot.Provider>;
}

/** The foot island of the setup, which the step on screen fills through SetupNav. */
const FooterSlot = createContext<HTMLElement | null>(null);

/**
 * The setup's Back and Continue (Brúnó, 2026-10-11: "the buttons should be the
 * same and in the same place"): the same two buttons on every step, on one dark
 * island at the foot of the window, with the step count and what comes next on
 * its left. Continue is never greyed out: pressed with something missing, the
 * page points at what is missing, as a form does. Outside the setup there is no
 * island, and this renders nothing; the page keeps its own button.
 */
export function SetupNav({next, form, onContinue, busy = false}: {
  /** What Continue leads to, e.g. "Next: choose your plan". */
  next: string;
  /** The form Continue submits; without one, onContinue runs. */
  form?: string;
  onContinue?(): void;
  busy?: boolean;
}) {
  const slot = useContext(FooterSlot);
  const {pathname} = useLocation();
  if (!slot) return null;
  const at = Math.max(0, SETUP_STEPS.findIndex((item) => item.path === pathname));
  const back = at > 0 ? SETUP_STEPS[at - 1] : undefined;
  return createPortal(<div className="setup-nav">
    <div className="setup-nav-progress">
      <span className="setup-nav-count"><span className="setup-nav-bar" aria-hidden="true">{SETUP_STEPS.map((item, index) =>
        <i key={item.key} className={index <= at ? 'on' : undefined} />)}</span>Step {at + 1} of {SETUP_STEPS.length}</span>
      <b>{next}</b>
    </div>
    {back ? <Link className="btn setup-nav-back" to={back.path} aria-label={`Back to ${back.label}`}><Icon name="arrowLeft" size={17} /><span>Back</span></Link> : null}
    <button className="btn btn-g setup-nav-continue" type={form ? 'submit' : 'button'} form={form} onClick={form ? undefined : onContinue}
      disabled={busy} aria-busy={busy || undefined}>Continue{busy ? <span className="setup-nav-spinner" aria-hidden="true" /> : <Icon name="arrowRight" size={17} />}</button>
  </div>, slot);
}
